// The slot machine. The server decides every spin; the spinning reels are only
// an animation that stops on the result the server already sent.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api, ApiError } from '../api.ts';
import { useGame, money, fmt } from '../state.tsx';
import { Loading, Panel } from '../components/ui.tsx';
import { play, type SoundHandle } from '../fx/sound.ts';
import { fx } from '../fx/fx.tsx';

const SYMBOLS = ['cherry', 'lemon', 'bell', 'bar', 'seven', 'diamond'];

export function SlotSymbol({ s, size = 70 }: { s: string; size?: number }) {
  const art: Record<string, ReactNode> = {
    cherry: (
      <g>
        <path d="M32 14 q-4 14 -12 22 M32 14 q6 14 14 18" stroke="#3a7a1a" strokeWidth="3" fill="none" />
        <path d="M32 14 q8 -6 16 -2 q-8 4 -16 2z" fill="#4a9a2a" />
        <circle cx="20" cy="42" r="10" fill="#d01020" stroke="#600" strokeWidth="1.5" />
        <circle cx="45" cy="38" r="10" fill="#d01020" stroke="#600" strokeWidth="1.5" />
        <circle cx="16" cy="38" r="3" fill="#fff" opacity="0.6" /><circle cx="41" cy="34" r="3" fill="#fff" opacity="0.6" />
      </g>
    ),
    lemon: (
      <g>
        <ellipse cx="32" cy="34" rx="22" ry="15" fill="#f2d21a" stroke="#a08a00" strokeWidth="1.5" />
        <path d="M10 34 l-4 -2 M54 34 l4 2" stroke="#a08a00" strokeWidth="3" />
        <ellipse cx="25" cy="28" rx="7" ry="3" fill="#fff" opacity="0.5" />
      </g>
    ),
    bell: (
      <g>
        <path d="M32 10 q14 0 15 18 q1 12 7 16 h-44 q6 -4 7 -16 q1 -18 15 -18z" fill="#f0b820" stroke="#7a5000" strokeWidth="1.5" />
        <rect x="9" y="44" width="46" height="5" rx="2" fill="#d09810" stroke="#7a5000" />
        <circle cx="32" cy="53" r="5" fill="#7a5000" />
        <path d="M24 18 q-4 8 -4 20" stroke="#fff" strokeWidth="3" opacity="0.5" fill="none" />
      </g>
    ),
    bar: (
      <g>
        <rect x="6" y="20" width="52" height="24" rx="3" fill="#111" stroke="#d4a017" strokeWidth="2" />
        <text x="32" y="39" textAnchor="middle" fontFamily="Arial Black, Arial, sans-serif" fontWeight="900" fontSize="17" fill="#fff">BAR</text>
      </g>
    ),
    seven: <text x="32" y="54" textAnchor="middle" fontFamily="Georgia, serif" fontWeight="bold" fontSize="54" fill="#d01020" stroke="#600" strokeWidth="2">7</text>,
    diamond: (
      <g>
        <path d="M14 24 l8 -12 h20 l8 12 l-18 28z" fill="#5ab8ff" stroke="#0a4a8a" strokeWidth="1.5" />
        <path d="M14 24 h36 M22 12 l10 12 l10 -12 M32 24 v28" stroke="#fff" opacity="0.6" fill="none" />
      </g>
    ),
  };
  return <svg width={size} height={size} viewBox="0 0 64 64" aria-label={s}>{art[s]}</svg>;
}

export function SlotsMachine() {
  const { me, setMe, setFlash, content } = useGame();
  const [info, setInfo] = useState<any>(null);
  const [pool, setPool] = useState<number | null>(null);
  const [bet, setBet] = useState('');
  const [reels, setReels] = useState<string[]>(['seven', 'seven', 'seven']);
  const [spinning, setSpinning] = useState<boolean[]>([false, false, false]);
  const [result, setResult] = useState<any>(null);
  const timers = useRef<number[]>([]);
  const [landed, setLanded] = useState<boolean[]>([false, false, false]);
  const [party, setParty] = useState<'' | 'win' | 'jackpot'>('');
  const spinSound = useRef<SoundHandle | null>(null);

  useEffect(() => {
    api.get('/api/casino/slots').then((d) => {
      setInfo(d);
      setBet(String(Math.min(d.maxBet, 1000)));
    });
    return () => timers.current.forEach((t) => clearInterval(t));
  }, []);
  if (!info || !me) return <Loading />;
  const busy = spinning.some(Boolean);
  const betNum = Math.floor(Number(bet.replace(/[^0-9]/g, '')));

  const spin = async () => {
    if (busy) return;
    setResult(null);
    setParty('');
    setLanded([false, false, false]);
    setSpinning([true, true, true]);
    spinSound.current?.stop();
    spinSound.current = play('spin', { duration: 1.4 });
    // Pure animation: show random symbols while waiting for the server's answer.
    const anim = window.setInterval(() => setReels(() => [0, 1, 2].map(() => SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)])), 70);
    timers.current.push(anim);
    let r: any;
    try {
      r = await api.post('/api/casino/slots/spin', { bet: betNum });
    } catch (e) {
      clearInterval(anim);
      setSpinning([false, false, false]);
      spinSound.current?.stop();
      play('error');
      setFlash({ kind: 'bad', text: e instanceof ApiError ? e.message : 'Something went wrong.' });
      return;
    }
    // Stop the reels one at a time on the server's result.
    const final: string[] = r.result.reels;
    const stopAt = [500, 850, 1200];
    stopAt.forEach((ms, i) =>
      setTimeout(() => {
        setSpinning((s) => s.map((v, j) => (j === i ? false : v)));
        setLanded((s) => s.map((v, j) => (j === i ? true : v)));
        play('reelStop');
        if (i === 2) {
          clearInterval(anim);
          spinSound.current?.stop();
          setReels(final);
          setResult(r.result);
          setPool(r.result.pool);
          setMe(r.me);
          const res = r.result;
          if (res.jackpot > 0) { play('jackpot'); fx.coins(30); setParty('jackpot'); }
          else if (res.net > 0 && res.multiplier >= 10) { play('bigWin'); fx.coins(18); setParty('win'); }
          else if (res.net > 0) { play('win'); setParty('win'); }
          else if (res.multiplier > 0) play('coin');
          else play('lose', { volume: 0.6 });
          if (res.net !== 0) fx.float(`${res.net > 0 ? '+' : '−'}${money(Math.abs(res.net))}`, res.net > 0 ? 'cash' : 'loss');
          window.setTimeout(() => setParty(''), res.jackpot > 0 ? 4000 : 2000);
        }
      }, ms),
    );
    setTimeout(() => setReels((cur) => cur.map((c, j) => (j === 0 ? final[0] : c))), stopAt[0]);
    setTimeout(() => setReels((cur) => cur.map((c, j) => (j <= 1 ? final[j] : c))), stopAt[1]);
  };

  const pays = info.paytable;
  const chips = [100, 1000, 10_000, 100_000, 1_000_000].filter((c) => c <= info.maxBet);
  const chipColor: Record<number, string> = { 100: '#2a6ad8', 1000: '#222', 10_000: '#a01010', 100_000: '#6a2a8a', 1_000_000: '#b8860b' };

  return (
    <div className="row casino-split" style={{ alignItems: 'flex-start' }}>
      <div className="grow">
        <div className={`slot-machine ${party ? `party-${party}` : ''}`}>
          <div className="slot-top">★ LUCKY SEVENS ★</div>
          <div className="jackpot-meter" title={`Grows by ${content.rules.slotsJackpotPct}% of every bet. Three diamonds wins it (bet ${money(content.rules.slotsJackpotFullBet)} or more for the whole pot).`}>
            <span>PROGRESSIVE JACKPOT</span>
            <b>{money(pool ?? info.pool)}</b>
          </div>
          <div className="slot-window">
            {reels.map((s, i) => (
              <div key={i} className={`slot-reel ${spinning[i] ? 'spinning' : ''} ${landed[i] && !spinning[i] ? 'landed' : ''}`}>
                {spinning[i] ? (
                  <div className="reel-strip" style={{ animationDuration: `${0.16 + i * 0.02}s` }}>
                    {[s, SYMBOLS[(SYMBOLS.indexOf(s) + 2) % SYMBOLS.length], SYMBOLS[(SYMBOLS.indexOf(s) + 4) % SYMBOLS.length], s].map((x, k) => <SlotSymbol key={k} s={x} />)}
                  </div>
                ) : <SlotSymbol s={s} />}
              </div>
            ))}
            <div className="payline" />
          </div>
          <div className={`slot-result ${result ? (result.net > 0 ? 'win' : 'lose') : ''}`}>
            {busy ? 'Spinning…' : result ? (result.net > 0 ? <>{result.label}! You win {money(result.payout)}{result.jackpot > 0 && <> including the {money(result.jackpot)} progressive jackpot!</>}</> : result.multiplier > 0 ? <>{result.label}: your bet back</> : 'No win. Try again!') : 'Place your bet and pull the lever'}
          </div>
          <div className="slot-controls">
            <div className="chips">
              {chips.map((c) => (
                <div key={c} className="chip" style={{ background: chipColor[c] }} onClick={() => !busy && setBet(String(Math.min(info.maxBet, (betNum || 0) + c)))} title={`Add ${money(c)}`}>
                  {c >= 1_000_000 ? '1M' : c >= 1000 ? `${c / 1000}K` : c}
                </div>
              ))}
            </div>
            <div style={{ marginTop: 6 }}>
              Bet $ <input value={bet} disabled={busy} onChange={(e) => setBet(e.target.value)} style={{ width: 110, fontSize: 13 }} />{' '}
              <a className="small" onClick={() => !busy && setBet(String(info.minBet))}>min</a> · <a className="small" onClick={() => !busy && setBet(String(Math.min(info.maxBet, me.cash)))}>max</a>{' '}
              <button className={`btn big ${!busy && betNum >= info.minBet && betNum <= me.cash ? '' : 'disabled'}`} disabled={busy} onClick={spin}>SPIN</button>
            </div>
            <div className="small muted" style={{ marginTop: 4 }}>Cash on hand: <span className="money">{money(me.cash)}</span> · limit at your level {money(info.maxBet)}</div>
          </div>
        </div>
      </div>
      <div className="side-col" style={{ width: 230 }}>
        <Panel title="Pays (× your bet)">
          <table className="list paytable">
            <tbody>
              {['diamond', 'seven', 'bar', 'cherry', 'bell', 'lemon'].map((s) => (
                <tr key={s}><td>{[0, 1, 2].map((i) => <SlotSymbol key={i} s={s} size={22} />)}</td><td className="right gold"><b>{pays.threeOfAKind[s]}×</b></td></tr>
              ))}
              <tr><td><SlotSymbol s="cherry" size={22} /><SlotSymbol s="cherry" size={22} /> <span className="small dim">first two reels</span></td><td className="right gold"><b>{pays.twoCherries}×</b></td></tr>
              <tr><td><SlotSymbol s="cherry" size={22} /> <span className="small dim">first reel</span></td><td className="right gold"><b>{pays.oneCherry}×</b></td></tr>
            </tbody>
          </table>
          <div className="small muted" style={{ marginTop: 6 }}>
            Wins include your bet back. Over time the machine pays back about {(info.returnToPlayer * 100).toFixed(1)}% of what's played, plus the progressive jackpot.
            Three diamonds also wins the jackpot: the whole pot with a bet of {money(content.rules.slotsJackpotFullBet)} or more, a share with smaller bets.
          </div>
          <div className="small dim" style={{ marginTop: 4 }}>Your spins: {fmt(me.slotSpins ?? info.spins)} · jackpots: {fmt(me.slotJackpots ?? info.jackpots)}</div>
        </Panel>
      </div>
    </div>
  );
}
